/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable import/prefer-default-export */

'use client';

import { ColumnDef, Row } from '@tanstack/react-table';
import { Eye, MoreHorizontal, PenLine, ReceiptText, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router';
import { Button } from '../../components/ui/button';
import PaymentStatusBadge from '../../components/ui/badge';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { IInvoice } from '../../types/types';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import useInvoice from '../../stores/useInvoice';

function DropdownInvoice({ row }: { row: Row<IInvoice> }) {
  const invoice = row.original;
  const { deleteInvoice } = useInvoice();

  return (
    <Dialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <Link to={`/invoice/view/${invoice.invoice_id}`}>
            <DropdownMenuItem>
              <Eye size={16} />
              View Invoice
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator />
          <Link to={`/invoice/edit/${invoice.invoice_id}`}>
            <DropdownMenuItem>
              <PenLine />
              Edit
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator />
          <Link to={`/receipt/create?partnerId=${invoice?.partner_id}&invoiceId=${invoice?.invoice_id}`}>
            <DropdownMenuItem>
              <ReceiptText />
              <span>Create receipt</span>
            </DropdownMenuItem>
          </Link>
          <DropdownMenuSeparator />
          <DialogTrigger className="w-full">
            <DropdownMenuItem>
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent className="w-[calc(100dvw-32px)] md:w-full rounded-lg">
        <DialogHeader>
          <DialogTitle>Confirm delete invoice</DialogTitle>
          <DialogDescription className="py-4">
            are you sure you want to delete invoice <span className="font-semibold">{invoice.invoice_number}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-row justify-end gap-2">
          <div>
            <Button variant="default" type="button" onClick={() => deleteInvoice(invoice?.invoice_id as string)}>
              Delete
            </Button>
          </div>
          <DialogClose>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const columns: ColumnDef<IInvoice>[] = [
  {
    accessorKey: 'invoice_number',
    header: 'Invoice No.',
    cell: ({ row }) => <div>{row.getValue('invoice_number')}</div>,
  },
  {
    accessorKey: 'client',
    header: () => <div>Client</div>,
    cell: ({ row }) => {
      const invoice = row.original;
      return <div className="font-medium">{invoice.partner?.name}</div>;
    },
  },
  {
    accessorKey: 'status_payment',
    header: () => <div>Payment Status</div>,
    cell: ({ row }) => <PaymentStatusBadge status={row.getValue('status_payment')} />,
  },
  {
    accessorKey: 'amount_due',
    header: () => <div className="text-center">Amount</div>,
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue('amount_due'));

      const formatted = new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
      }).format(amount);

      return <div className="text-right font-medium">{formatted}</div>;
    },
  },
  {
    accessorKey: 'date',
    header: () => <div>Invoice Date</div>,
    cell: ({ row }) => {
      const formattedDate = format(row.getValue('date'), 'LLL dd, y');
      return <div>{formattedDate}</div>;
    },
  },
  {
    accessorKey: 'due_date',
    header: () => <div>Due Date</div>,
    cell: ({ row }) => {
      const formattedDate = format(row.getValue('due_date'), 'LLL dd, y');
      return <div>{formattedDate}</div>;
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => <DropdownInvoice row={row} />,
  },
];
